import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as ApiService from "@/services/ApiService";
import {
  getFacilityKeyForPortal,
  getPatientKey,
  useProfileStore,
} from "./profile";
import { parseApiBoolResult } from "@/utils/parseApiBoolResult";
import { normalizeSexFromApi } from "@/utils/demographicsSex";
import type { ApiBoolResult } from "@/types/task-actions";

export enum DemographicsField {
  firstName = "firstName",
  mi = "mi",
  lastName = "lastName",
  nickname = "nickname",
  dob = "dob",
  sex = "sex",
  maritalStatID = "maritalStatID",
  address = "address",
  address2 = "address2",
  city = "city",
  state = "state",
  zip = "zip",
  mobilePhone = "mobilePhone",
  email = "email",
  contactMethod = "contactMethod",
  emerContact = "emerContact",
  emerRelation = "emerRelation",
  emerPhone = "emerPhone",
  employmentStatus = "employmentStatus",
  employStatID = "employStatID",
  homePhone = "homePhone",
}

export interface IDemographics {
  firstName: string;
  mi: string;
  lastName: string;
  nickname: string;
  dob: string;
  sex: string;
  maritalStatID: number | string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  mobilePhone: string;
  email: string;
  contactMethod: string;
  emerContact: string;
  emerRelation: string;
  emerPhone: string;
  employmentStatus: string;
  employStatID: number | string;
  homePhone: string;
}

function mapLegacyGetDemographicsToIDemographics(
  raw: Record<string, unknown>
): IDemographics {
  const sexRaw =
    raw?.sex ??
    raw?.Sex ??
    raw?.gender ??
    raw?.Gender ??
    raw?.patientSex ??
    raw?.PatientSex;
  return {
    firstName: String(raw?.firstName ?? ""),
    mi: String(raw?.mi ?? ""),
    lastName: String(raw?.lastName ?? ""),
    nickname: String(raw?.nickname ?? ""),
    dob: String(raw?.dob ?? ""),
    sex: normalizeSexFromApi(sexRaw),
    maritalStatID: (raw?.maritalStatID as number) ?? "",
    address: String(raw?.address ?? ""),
    address2: String(raw?.address2 ?? ""),
    city: String(raw?.city ?? ""),
    state: String(raw?.state ?? ""),
    zip: String(raw?.zip ?? ""),
    mobilePhone: String(
      raw?.mobilePhone ?? raw?.cellPhone ?? raw?.phone ?? ""
    ),
    email: String(raw?.email ?? ""),
    contactMethod: String(raw?.contactMethod ?? ""),
    emerContact: String(raw?.emerContact ?? ""),
    emerRelation: String(raw?.emerRelation ?? ""),
    emerPhone: String(raw?.emerPhone ?? ""),
    employmentStatus: "",
    employStatID: (raw?.employStatID as number) ?? "",
    homePhone: String(raw?.homePhone ?? ""),
  };
}

export interface DemographicsConfig {
  genderList:
    | { code: string; description: string }[]
    | null;
  maritalStatusList:
    | { id: number; description: string }[]
    | null;
  usStates:
    | { id: number; code: string; name: string }[]
    | null;
  phoneTypes: string[] | null;
  contactMethod: string[] | null;
  emerRelations: string[] | null;
  employmentStatusList: string[] | null;
}

/** Re-export for callers that referenced `ApiBoolResult` from this module. */
export type { ApiBoolResult };

/** Intake API request body shape (`UpdateDemographicsQueryObj` in Swagger). */
export interface IntakeDemographicsPayload {
  firstName: string;
  mi?: string | null;
  lastName: string;
  suffix?: string | null;
  nickname?: string | null;
  dob?: string | null;
  sex: string;
  employStatID?: number;
  maritalStatID?: number;
  address?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  mobilePhone?: string | null;
  homePhone?: string | null;
  email?: string | null;
  contactMethod?: string | null;
  emerContact?: string | null;
  emerRelation?: string | null;
  emerPhone?: string | null;
}

interface DemographicsState {
  loading: boolean;
  error: string | null;
  demographics: IDemographics | null;
  config: DemographicsConfig;
  fetchDemographics: () => Promise<IDemographics>;
  updateDemographics: (payload: IDemographics) => Promise<IDemographics>;
  fetchDemographicsConfig: () => Promise<DemographicsConfig>;
}

const defaultConfig: DemographicsConfig = {
  genderList: null,
  maritalStatusList: null,
  usStates: null,
  phoneTypes: null,
  contactMethod: null,
  emerRelations: null,
  employmentStatusList: null,
};

export const useDemographicsStore = create<DemographicsState>()(
  persist(
    (set) => ({
      loading: false,
      error: null,
      demographics: null,
      config: defaultConfig,

      fetchDemographics: async () => {
        set({ loading: true, error: null });
        const facilityKey = getFacilityKeyForPortal();
        const patientKey = getPatientKey();
        if (!facilityKey || !patientKey) {
          const msg = !facilityKey
            ? "Facility is not configured."
            : "No patient selected.";
          set({ loading: false, error: msg });
          throw new Error(msg);
        }
        try {
          const response = await ApiService.get(
            `/Portal/GetDemographics/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
          );
          const data = mapLegacyGetDemographicsToIDemographics(
            response.data as Record<string, unknown>
          );
          set({ demographics: data });
          const sex = (data.sex ?? "").trim();
          const dob = (data.dob ?? "").trim();
          if (sex || dob) {
            useProfileStore.getState().patchCurrentPatient({
              ...(sex ? { sex } : {}),
              ...(dob ? { dob } : {}),
            });
          }
          return data;
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: msg });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateDemographics: async (payload) => {
        set({ loading: true, error: null });
        const facilityKey = getFacilityKeyForPortal();
        const patientKey = getPatientKey();
        if (!facilityKey || !patientKey) {
          const msg = !facilityKey
            ? "Facility is not configured."
            : "No patient selected.";
          set({ loading: false, error: msg });
          throw new Error(msg);
        }
        try {
          /**
           * `POST /Portal/UpdateDemographics` returns `ApiBoolResult` (`success`, `reason`).
           * On success, local `demographics` is updated from the payload we sent (response no longer returns the row).
           */
          const mobileDigits = String(payload.mobilePhone ?? "")
            .replace(/\D/g, "")
            .slice(0, 10);
          const homeDigits = String(payload.homePhone ?? "")
            .replace(/\D/g, "")
            .slice(0, 10);
          const body: IntakeDemographicsPayload = {
            firstName: payload.firstName ?? "",
            mi: payload.mi ?? "",
            lastName: payload.lastName ?? "",
            suffix: "",
            nickname: payload.nickname ?? "",
            dob: payload.dob ?? "",
            sex: payload.sex ?? "",
            employStatID: Number(payload.employStatID) || 0,
            maritalStatID: Number(payload.maritalStatID) || 0,
            address: payload.address ?? "",
            address2: payload.address2 ?? "",
            city: payload.city ?? "",
            state: payload.state ?? "",
            zip: String(payload.zip ?? "").replace(/\D/g, "").slice(0, 9) || "",
            mobilePhone: mobileDigits,
            homePhone: homeDigits,
            email: payload.email ?? "",
            contactMethod: payload.contactMethod ?? "",
            emerContact: payload.emerContact ?? "",
            emerRelation: payload.emerRelation ?? "",
            emerPhone: String(payload.emerPhone ?? "").replace(/\D/g, "").slice(0, 10),
          };
          if (import.meta.env.DEV) {
            console.log("[Portal/UpdateDemographics] payload", body);
          }
          const res = await ApiService.post(
            `/Portal/UpdateDemographics/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
            body
          );
          const { success, reason } = parseApiBoolResult(res.data);
          if (!success) {
            const msg =
              (reason && reason.trim()) || "Unable to update demographics.";
            set({ error: msg });
            throw new Error(msg);
          }
          set({ demographics: payload as IDemographics, error: null });
          const sexSaved = (body.sex ?? "").trim();
          const dobSaved = (body.dob ?? "").trim();
          if (sexSaved || dobSaved) {
            useProfileStore.getState().patchCurrentPatient({
              ...(sexSaved ? { sex: sexSaved } : {}),
              ...(dobSaved ? { dob: dobSaved } : {}),
            });
          }
          return payload as IDemographics;
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: msg });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      fetchDemographicsConfig: async () => {
        set({ loading: true, error: null });
        try {
          const response = await ApiService.get("/Portal/DemographicsConfig");
          const data = response.data as DemographicsConfig;
          set({ config: data });
          return data;
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : "An error occurred";
          set({ error: msg });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

    }),
    { name: "demographics" }
  )
);
